import { useEffect } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { NotesTextEditorHandle, NotesTextEditor } from '@factorialco/f0-react/dist/experimental'
import { Ai } from '@factorialco/f0-react/icons/app'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { MeetingSkeleton } from 'modules/meetings/components/Skeletons/MeetingSkeleton'
import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'
import { useNotesTextEditorConfig } from 'modules/meetings/lib/hooks/useNotesTextEditorConfig'
import { formatTimeAgo } from 'modules/meetings/lib/utils/dates'

interface MeetingNotesProps {
  textEditorRef: React.RefObject<NotesTextEditorHandle> | null | undefined
  meetingState: 'pre' | 'post' | null | undefined
  toggleAiChat?: () => void
  haveTranscript?: boolean
  hideNotes?: boolean
}

export const MeetingNotes = ({
  textEditorRef,
  meetingState,
  toggleAiChat,
  haveTranscript = false,
  hideNotes = false,
}: MeetingNotesProps) => {
  const { page, loading, canUseAI, meeting, setNotes, notes, meetingId } = useMeeting()

  const {
    access: { locale },
  } = useCurrentSession()

  const { titlePlaceholder, aiBlockConfig } = useNotesTextEditorConfig({
    meetingId: meetingId ?? undefined,
    meetingState: meetingState ?? undefined,
  })

  const isCorrectMeetingLoaded = meeting?.id === Number(meetingId)
  const isStillLoading =
    loading || !page || notes === null || !meetingState || !isCorrectMeetingLoaded

  useEffect(() => {
    if (!isStillLoading && textEditorRef?.current) {
      textEditorRef.current.focus()
    }
  }, [isStillLoading, textEditorRef])

  if (isStillLoading) return <MeetingSkeleton />

  return (
    <Box
      overflowY="auto"
      gap="s16"
      height="full"
      flexGrow
      width="full"
      style={{
        cursor: 'text',
        display: hideNotes ? 'none' : undefined,
      }}
    >
      <Box overflowY="hidden" height="full">
        <NotesTextEditor
          key={meetingId}
          ref={textEditorRef}
          titlePlaceholder={titlePlaceholder}
          placeholder={i18n.t('meetings.notes.placeholder')}
          onChange={({ json }) => {
            const jsonString = JSON.stringify(json)
            setNotes(jsonString)
          }}
          initialEditorState={{
            content: notes && notes.trim() !== '' ? JSON.parse(notes) : undefined,
            title: page?.title,
          }}
          aiBlockConfig={aiBlockConfig}
          metadata={[
            {
              label: i18n.t('meetings.notes.private_notes_label'),
              value: {
                type: 'status',
                label: i18n.t('meetings.notes.private_notes_value'),
                variant: 'neutral',
              },
            },
            {
              label: i18n.t('meetings.notes.last_edited'),
              value: {
                type: 'text',
                content: formatTimeAgo(page?.updatedAt, locale),
              },
            },
          ]}
          secondaryActions={[
            canUseAI &&
              toggleAiChat &&
              !haveTranscript && {
                label: i18n.t('meetings.meeting.chat.title'),
                icon: Ai,
                onClick: toggleAiChat,
              },
          ].filter(Boolean)}
        />
      </Box>
    </Box>
  )
}
